import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { PurchaseOrder } from '../../hooks/useCompras'
import { useCompanyBudget } from '../../hooks/useCompanyBudget'
import { useInstallments } from '../../hooks/useInstallments'
import { useCostOfGoods, costRealByMonth } from '../../hooks/useCostOfGoods'
import { suggestMonthlyCap } from '../../lib/budgetSuggestion'
import { fmtBRL } from '../../lib/format'

interface Props {
  orders: PurchaseOrder[]
  /** Mês corrente em YYYY-MM (ex.: "2026-06"); usado para filtrar POs por created_at. */
  monthKey: string
}

/**
 * Cap mensal de compras com duas leituras:
 *   - **Caixa do mês** (fonte principal): soma das parcelas com due_date no
 *     mês. Premia negociar prazos longos para urgentes.
 *   - **Pedido emitido**: soma de total_value dos POs criados no mês (Sprint 1).
 *     Fica como sub-info para auditoria.
 *
 * Inputs inline editam orçamento e dia de corte (companies.*).
 */
export default function OrcamentoMensalCard({ orders, monthKey }: Props) {
  const { budget, loading, saving, update } = useCompanyBudget()
  const { sumNext } = useInstallments()
  // Sugestão do teto + base do cap pela Custo de Mercadoria (base matriz).
  const { entries: cogEntries } = useCostOfGoods()
  const capSugerido = useMemo(
    () => suggestMonthlyCap(cogEntries, { months: 6, defaultTargetPct: budget.cost_target_pct }),
    [cogEntries, budget.cost_target_pct],
  )
  const costRealMap = useMemo(() => costRealByMonth(cogEntries), [cogEntries])
  const [editingValor, setEditingValor] = useState(false)
  const [editingCorte, setEditingCorte] = useState(false)
  const [editingMeta, setEditingMeta] = useState(false)
  const [draftValor, setDraftValor] = useState('')
  const [draftCorte, setDraftCorte] = useState('')
  const [draftMeta, setDraftMeta] = useState('')

  const pedidoMes = useMemo(() => {
    return orders.reduce((sum, po) => {
      if (po.status === 'Cancelado') return sum
      if (!po.created_at) return sum
      if (po.created_at.slice(0, 7) !== monthKey) return sum
      return sum + (Number(po.total_value) || 0)
    }, 0)
  }, [orders, monthKey])

  // Base do cap = CUSTO DE MERCADORIA do mês (como o usuário gerencia o teto), não
  // as parcelas. custoMes = custo real lançado no mês corrente (base matriz).
  const custoMes = costRealMap.get(monthKey) ?? 0
  const caixaProximos90 = sumNext(90) // tesouraria (parcelas) — sub-info

  const teto = budget.purchase_budget_monthly
  const disponivel = Math.max(0, teto - custoMes)
  const pct = teto > 0 ? Math.min(100, (custoMes / teto) * 100) : 0
  const ativo = teto > 0

  // Cap rolante: disponibilidade do mês corrente + 2 (ex.: jun/jul/ago).
  const proximosMeses = useMemo(() => {
    const labels = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez']
    const [y, m] = monthKey.split('-').map(Number)
    return [0, 1, 2].map(n => {
      const d = new Date(y, m - 1 + n, 1)
      const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
      const comprometido = costRealMap.get(mk) ?? 0
      return {
        mk,
        label: labels[d.getMonth()],
        atual: n === 0,
        comprometido,
        disponivel: Math.max(0, teto - comprometido),
        pct: teto > 0 ? Math.min(100, (comprometido / teto) * 100) : 0,
      }
    })
  }, [monthKey, costRealMap, teto])

  const tone = !ativo
    ? 'border-line bg-surface'
    : pct >= 90
      ? 'border-red-400/40 bg-red-50 dark:bg-red-500/10 dark:border-red-500/30'
      : pct >= 70
        ? 'border-amber-400/40 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/30'
        : 'border-emerald-400/40 bg-emerald-50 dark:bg-emerald-500/10 dark:border-emerald-500/30'

  const barColor = !ativo
    ? 'bg-line'
    : pct >= 90 ? 'bg-red-500'
    : pct >= 70 ? 'bg-amber-500'
    : 'bg-emerald-500'

  const today = new Date()
  const isCurrentMonth = today.toISOString().slice(0, 7) === monthKey
  const aposCorte = isCurrentMonth && today.getDate() >= budget.purchase_cutoff_day

  async function saveValor() {
    const n = Number(draftValor.replace(/\./g, '').replace(',', '.'))
    if (Number.isFinite(n) && n >= 0) await update({ purchase_budget_monthly: n })
    setEditingValor(false)
  }
  async function saveCorte() {
    const n = Number(draftCorte)
    if (Number.isInteger(n) && n >= 1 && n <= 28) await update({ purchase_cutoff_day: n })
    setEditingCorte(false)
  }
  async function saveMeta() {
    const n = Number(draftMeta.replace(',', '.'))
    if (Number.isFinite(n) && n > 0 && n <= 100) await update({ cost_target_pct: n })
    setEditingMeta(false)
  }

  return (
    <section className={`rounded-xl border px-4 py-3 ${tone}`}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="text-xs font-semibold uppercase tracking-wide text-ink-faint">
            Cap mensal de compras — custo de mercadoria
          </h3>
          <p className="text-[11px] text-ink-faint">
            Custo de mercadoria lançado no mês (base matriz) vs teto. Disponível = teto − custo real do mês.
          </p>
        </div>
        {aposCorte && (
          <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-amber-100 text-amber-800 dark:bg-amber-500/15 dark:text-amber-300 border border-amber-300 dark:border-amber-500/30">
            Após dia de corte ({budget.purchase_cutoff_day}) — novos pedidos vão para virada
          </span>
        )}
      </div>

      <div className="mt-3 grid grid-cols-1 sm:grid-cols-[1fr,auto,auto,auto] gap-4 items-end">
        {/* Barra de progresso */}
        <div>
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-ink-muted">
              Custo de mercadoria do mês <span className="font-bold text-ink tabular-nums">{fmtBRL(custoMes)}</span>
              {ativo && <> <span className="text-ink-faint">/ {fmtBRL(teto)}</span></>}
            </span>
            {ativo && (
              <span className="text-xs text-ink-muted">
                Disponível <span className="font-bold text-ink tabular-nums">{fmtBRL(disponivel)}</span>
              </span>
            )}
          </div>
          <div className="mt-1 h-2 rounded-full bg-sunken overflow-hidden">
            <div className={`h-full ${barColor} transition-all`} style={{ width: `${pct}%` }} />
          </div>
          <p className="mt-1 text-[11px] text-ink-faint">
            Pedido emitido este mês: <span className="text-ink-muted font-semibold tabular-nums">{fmtBRL(pedidoMes)}</span>
            <span className="mx-1.5">·</span>
            A pagar nos próximos 90 dias: <span className="text-ink-muted font-semibold tabular-nums">{fmtBRL(caixaProximos90)}</span>
          </p>
          {!ativo && (
            <p className="mt-1 text-[11px] text-ink-faint">Sem teto definido — cadastre um orçamento para ativar.</p>
          )}
        </div>

        {/* Orçamento (input inline) */}
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-ink-faint">Orçamento R$</span>
          {editingValor ? (
            <input
              autoFocus
              type="text"
              inputMode="decimal"
              defaultValue={String(teto)}
              onChange={e => setDraftValor(e.target.value)}
              onBlur={saveValor}
              onKeyDown={e => { if (e.key === 'Enter') saveValor() }}
              disabled={saving}
              className="w-32 bg-paper border border-brand-400 rounded px-2 py-1 text-right text-ink text-sm focus:outline-none"
            />
          ) : (
            <button
              onClick={() => { setDraftValor(String(teto)); setEditingValor(true) }}
              disabled={loading}
              className="w-32 text-right tabular-nums px-2 py-1 rounded border border-line bg-paper hover:border-brand-400 text-ink text-sm transition-colors disabled:opacity-50"
              title="Clique para editar"
            >
              {fmtBRL(teto)}
            </button>
          )}
        </label>

        {/* Meta de custo % (input inline) — âncora do teto sugerido */}
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-ink-faint">Meta custo %</span>
          {editingMeta ? (
            <input
              autoFocus
              type="text"
              inputMode="decimal"
              defaultValue={String(budget.cost_target_pct)}
              onChange={e => setDraftMeta(e.target.value)}
              onBlur={saveMeta}
              onKeyDown={e => { if (e.key === 'Enter') saveMeta() }}
              disabled={saving}
              className="w-20 bg-paper border border-brand-400 rounded px-2 py-1 text-right text-ink text-sm focus:outline-none"
            />
          ) : (
            <button
              onClick={() => { setDraftMeta(String(budget.cost_target_pct)); setEditingMeta(true) }}
              disabled={loading}
              className="w-20 text-right tabular-nums px-2 py-1 rounded border border-line bg-paper hover:border-brand-400 text-ink text-sm transition-colors disabled:opacity-50"
              title="Meta de custo de mercadoria (% do faturamento). Âncora do teto sugerido para meses sem meta própria lançada."
            >
              {budget.cost_target_pct}%
            </button>
          )}
        </label>

        {/* Dia de corte (input inline) */}
        <label className="flex flex-col gap-0.5">
          <span className="text-[10px] uppercase tracking-wide text-ink-faint">Dia de corte</span>
          {editingCorte ? (
            <input
              autoFocus
              type="number"
              min={1}
              max={28}
              defaultValue={budget.purchase_cutoff_day}
              onChange={e => setDraftCorte(e.target.value)}
              onBlur={saveCorte}
              onKeyDown={e => { if (e.key === 'Enter') saveCorte() }}
              disabled={saving}
              className="w-20 bg-paper border border-brand-400 rounded px-2 py-1 text-right text-ink text-sm focus:outline-none"
            />
          ) : (
            <button
              onClick={() => { setDraftCorte(String(budget.purchase_cutoff_day)); setEditingCorte(true) }}
              disabled={loading}
              className="w-20 text-right tabular-nums px-2 py-1 rounded border border-line bg-paper hover:border-brand-400 text-ink text-sm transition-colors disabled:opacity-50"
              title="Dia 1..28; depois disso, novos pedidos vão como Standby"
            >
              dia {budget.purchase_cutoff_day}
            </button>
          )}
        </label>
      </div>

      {/* Cap rolante: disponibilidade dos próximos meses (corrente + 2) */}
      {ativo && (
        <div className="mt-3 grid grid-cols-3 gap-2">
          {proximosMeses.map(pm => {
            const cor = pm.pct >= 90 ? 'bg-red-500' : pm.pct >= 70 ? 'bg-amber-500' : 'bg-emerald-500'
            const esgotado = pm.disponivel <= 0
            return (
              <div key={pm.mk} className="rounded-lg border border-line bg-paper px-2.5 py-1.5">
                <p className="text-[10px] uppercase tracking-wide text-ink-faint">
                  <span className="capitalize">{pm.label}</span>{pm.atual ? ' · atual' : ''}
                </p>
                <p className={`text-sm font-bold tabular-nums leading-tight ${esgotado ? 'text-red-500' : 'text-ink'}`}>
                  {esgotado ? 'esgotado' : `${fmtBRL(pm.disponivel)} livre`}
                </p>
                <div className="mt-1 h-1 rounded-full bg-sunken overflow-hidden">
                  <div className={`h-full ${cor}`} style={{ width: `${pm.pct}%` }} />
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Sugestão do teto a partir da Custo de Mercadoria (meta de custo) */}
      {capSugerido.value != null ? (
        <p className="mt-3 text-[11px] text-ink-faint">
          <span title="Média de (faturamento × meta%) dos últimos meses na Custo de Mercadoria (base matriz).">
            💡 Sugestão (meta de custo · {capSugerido.monthsUsed}m):{' '}
            <span className="font-semibold text-ink-muted tabular-nums">{fmtBRL(capSugerido.value)}</span>
          </span>
          {capSugerido.value !== teto && (
            <button
              onClick={() => update({ purchase_budget_monthly: capSugerido.value! })}
              disabled={saving}
              className="ml-2 text-brand-700 dark:text-brand-300 underline hover:text-ink disabled:opacity-50"
            >
              Usar
            </button>
          )}
        </p>
      ) : (
        <p className="mt-3 text-[11px] text-ink-faint">
          💡 <Link to="/custo-mercadoria" className="underline hover:text-ink-muted">Preencha a Custo de Mercadoria</Link> para sugerir o teto automaticamente.
        </p>
      )}
    </section>
  )
}
